// src/pages/MembersPage.js — [marker: data-grid v1]
// Excel-style grid of every business: sort/filter any column, show/hide and
// resize columns (saved per staff member), export.
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import DataGrid from '../components/DataGrid';

const human = (s) => (s ? String(s).replace(/_/g, ' ').replace(/\b\w/g, m => m.toUpperCase()) : '');

const MEMBER_COLUMNS = [
  { key: 'business_name', label: 'Business', width: 230, render: r => <strong>{r.business_name}</strong> },
  { key: 'membership_type_name', label: 'Membership', width: 140 },
  { key: 'annual_dues', label: 'Dues', width: 95, type: 'money', defaultHidden: true },
  { key: 'status', label: 'Status', width: 115, text: r => human(r.status),
    render: r => <span className={`badge badge-${r.status}`} style={{ fontSize: '0.65rem' }}>{human(r.status)}</span> },
  { key: 'renewal_month', label: 'Renewal Month', width: 115 },
  { key: 'next_renewal_date', label: 'Next Renewal', width: 110, type: 'date' },
  { key: 'last_renewal_date', label: 'Last Renewal', width: 110, type: 'date', defaultHidden: true },
  { key: 'member_since', label: 'Member Since', width: 110, type: 'date', defaultHidden: true },
  { key: 'primary_contact', label: 'Main Contact', width: 160 },
  { key: 'primary_contact_email', label: 'Main Contact Email', width: 200, defaultHidden: true },
  { key: 'email', label: 'Business Email', width: 200 },
  { key: 'phone', label: 'Phone', width: 125 },
  { key: 'fax', label: 'Fax', width: 120, defaultHidden: true },
  { key: 'website', label: 'Website', width: 170, defaultHidden: true },
  { key: 'contact_count', label: 'Contacts', width: 85, type: 'number' },
  { key: 'outstanding_balance', label: 'Outstanding', width: 110, type: 'money' },
  { key: 'total_paid', label: 'Total Paid', width: 110, type: 'money', defaultHidden: true },
  { key: 'salesperson', label: 'Salesperson', width: 110, get: r => r.salesperson_initials || '', text: r => r.salesperson_name || r.salesperson_initials || '' },
  { key: 'industry_name', label: 'Industry', width: 140, defaultHidden: true },
  { key: 'address_line1', label: 'Address', width: 200, defaultHidden: true },
  { key: 'city', label: 'City', width: 110, defaultHidden: true },
  { key: 'state', label: 'State', width: 70, defaultHidden: true },
  { key: 'zip', label: 'Zip', width: 80, defaultHidden: true },
  { key: 'notes', label: 'Notes', width: 220, defaultHidden: true },
  { key: 'created_at', label: 'Added', width: 100, type: 'date', defaultHidden: true },
];

export default function MembersPage() {
  const { isManagerOrAbove } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [memberTypes, setMemberTypes] = useState([]);
  const [salespersons, setSalespersons] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);

  const fetchMembers = () => {
    setLoading(true);
    api.get('/businesses/grid')
      .then(res => setRows(res.data || []))
      .catch(() => toast.error('Failed to load members.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchMembers(); }, []);

  useEffect(() => {
    Promise.all([
      api.get('/lookups/membership-types'),
      api.get('/lookups/salespersons'),
    ]).then(([mt, sp]) => {
      setMemberTypes(mt.data);
      setSalespersons(sp.data);
    }).catch(() => {});
  }, []);

  return (
    <div>
      <div className="page-header" style={{ marginBottom: 12 }}>
        <div>
          <h1 className="page-title">Members</h1>
          <p className="page-subtitle">All businesses, including non-members and archived — filter the Status column to narrow.</p>
        </div>
        {isManagerOrAbove() && (
          <button className="btn btn-primary" onClick={() => setShowAddModal(true)}>
            + Add Member
          </button>
        )}
      </div>

      <DataGrid
        gridKey="members"
        columns={MEMBER_COLUMNS}
        rows={rows}
        loading={loading}
        onRowClick={r => navigate(`/members/${r.id}`)}
      />

      {showAddModal && <AddMemberModal onClose={() => setShowAddModal(false)} onSaved={() => { setShowAddModal(false); fetchMembers(); }} memberTypes={memberTypes} salespersons={salespersons} />}
    </div>
  );
}

function AddMemberModal({ onClose, onSaved, memberTypes, salespersons }) {
  const [form, setForm] = useState({ businessName: '', email: '', phone: '', membershipTypeId: '', renewalMonth: 'January', salespersonId: '', status: 'active' });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.businessName) return toast.error('Business name is required.');
    setSaving(true);
    try {
      await api.post('/businesses', form);
      toast.success('Member added.');
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to add member.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <span className="modal-title">Add New Member</span>
          <button className="btn btn-ghost btn-icon" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Business Name *</label>
              <input className="form-input" value={form.businessName} onChange={e => set('businessName', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Email</label>
              <input className="form-input" type="email" value={form.email} onChange={e => set('email', e.target.value)} />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Phone</label>
              <input className="form-input" value={form.phone} onChange={e => set('phone', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Membership Type</label>
              <select className="form-select" value={form.membershipTypeId} onChange={e => set('membershipTypeId', e.target.value)}>
                <option value="">Select type</option>
                {memberTypes.map(mt => <option key={mt.id} value={mt.id}>{mt.name} — ${mt.annual_dues}/yr</option>)}
              </select>
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Renewal Month</label>
              <select className="form-select" value={form.renewalMonth} onChange={e => set('renewalMonth', e.target.value)}>
                <option value="January">January</option>
                <option value="July">July</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Salesperson</label>
              <select className="form-select" value={form.salespersonId} onChange={e => set('salespersonId', e.target.value)}>
                <option value="">Select</option>
                {salespersons.map(sp => <option key={sp.id} value={sp.id}>{sp.initials} — {sp.full_name}</option>)}
              </select>
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Add Member'}
          </button>
        </div>
      </div>
    </div>
  );
}

function fmtDate(d) {
  if (!d) return '';
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}
